import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * IncidentTranslation — faithful machine translation of the student's report text (FEAT-137 #137).
 *
 * The backend adds `descriptionTranslated` + `descriptionSourceLang` at read time (Amazon Translate)
 * ONLY when the detected source language differs from the responder's language — the ORIGINAL text is
 * never modified and is still shown in the incident details below. We clearly label this as a machine
 * translation (no interpretive AI between student and responder). Renders nothing when absent.
 */
const LANG_NAMES = { es: 'Spanish', en: 'English', fr: 'French', zh: 'Chinese', ar: 'Arabic', vi: 'Vietnamese', pt: 'Portuguese' };

const IncidentTranslation = ({ data }) => {
  const theme = useTheme();
  const translated = data && data.descriptionTranslated;
  if (!translated) return null;
  const src = data.descriptionSourceLang;
  const srcName = LANG_NAMES[src] || (src ? src.toUpperCase() : 'another language');

  const style = {
    container: {
      display: 'flex', flexDirection: 'column',
      marginLeft: '60px', marginRight: '60px', marginTop: '40px', width: 'auto',
      paddingLeft: '0px !important',
      border: `1px solid ${theme.palette.custom?.gray || '#ccc'}`, borderRadius: '4px',
    },
    header: { borderBottom: `1px solid ${theme.palette.custom?.gray || '#ccc'}` },
    title: { fontSize: '20px', fontWeight: 700, marginTop: '20px', marginBottom: '20px', marginLeft: '20px' },
    body: { padding: '20px' },
    meta: { fontSize: '13px', color: theme.palette.text?.secondary || '#666', marginBottom: '10px' },
    text: { fontSize: '15px', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  };

  return (
    <Container sx={style.container}>
      <Container sx={style.header}>
        <Typography sx={style.title}>Report — translated</Typography>
      </Container>
      <Box sx={style.body}>
        <Typography sx={style.meta}>
          Machine-translated from {srcName} to English. The student's original words are shown in the
          incident details below — this translation is a convenience, not a substitute for the original.
        </Typography>
        <Typography sx={style.text}>{translated}</Typography>
      </Box>
    </Container>
  );
};

export default IncidentTranslation;
